import { useEffect, useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';

import type { AdHocFileChunk } from '../../../../../../../../backend/src/matter/ad-hoc/ad-hoc-file-chunk.entity';
import { PageBoundingBox } from '../../../../../../../../backend/src/matter/fact/fact-bounding-box';
import { useGetProceduralFile } from '@/api/queries/useGetProceduralFile';
import { Icon } from '@/new/components/icon';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { ResizableSidePanel } from '@/new/components/resizable-side-panel';
import { Button } from '@/new/components/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { BoundingBoxWithId } from '@/types';

export interface ProceduralSourceRef {
  fileId: string;
  fileName: string;
  pageNumber: number;
  chunkId: string;
  content: string;
  boundingBox: PageBoundingBox;
}

export interface SourceCitation {
  ref: ProceduralSourceRef;
  label: string;
  color: string;
}

/** A chunk's source ref, or null if it has no file. */
export function chunkToSourceRef(chunk: AdHocFileChunk | null | undefined): ProceduralSourceRef | null {
  if (!chunk?.file) return null;
  return {
    fileId: chunk.file.id,
    fileName: chunk.file.name,
    pageNumber: chunk.pageNumber,
    chunkId: chunk.id,
    content: chunk.content,
    boundingBox: chunk.boundingBox,
  };
}

/** Pick a source ref from chunks, preferring one in `preferFileId` (shows inline); else the last. */
export function sourceRefFromChunks(
  chunks: AdHocFileChunk[] | null | undefined,
  preferFileId?: string,
): ProceduralSourceRef | null {
  if (chunks === undefined || chunks === null || chunks.length === 0) return null;
  const preferred =
    preferFileId !== undefined ? [...chunks].reverse().find((c) => c.file?.id === preferFileId) : undefined;
  return chunkToSourceRef(preferred ?? chunks[chunks.length - 1]);
}

interface ProceduralSourcePanelProps {
  /** Citations to show; the first is the default document. */
  citations: SourceCitation[];
  onClose: () => void;
  dismissable?: boolean;
}

/**
 * Inline, resizable source panel sitting beside the timeline. Citations in the
 * same document render together as coloured highlights; those in other
 * documents become entries in a document switcher.
 */
export function ProceduralSourcePanel({ citations, onClose, dismissable = true }: ProceduralSourcePanelProps) {
  return (
    <AnimatePresence initial={false}>
      {citations.length > 0 && (
        <ResizableSidePanel
          storageKey="procedural-source"
          onClose={onClose}
          className="rounded-lg border"
          disableDrawerMode
        >
          <ProceduralSourcePanelBody citations={citations} onClose={onClose} dismissable={dismissable} />
        </ResizableSidePanel>
      )}
    </AnimatePresence>
  );
}

interface PanelDoc {
  fileId: string;
  fileName: string;
  labels: string[];
}

export function ProceduralSourcePanelBody({
  citations,
  onClose,
  dismissable,
}: {
  citations: SourceCitation[];
  onClose: () => void;
  dismissable: boolean;
}) {
  const { matterId } = useParams();
  const primary = citations[0];

  // Distinct documents across all citations, in first-seen order.
  const docs = useMemo<PanelDoc[]>(() => {
    const byFile = new Map<string, PanelDoc>();
    for (const c of citations) {
      const existing = byFile.get(c.ref.fileId);
      if (existing) existing.labels.push(c.label);
      else byFile.set(c.ref.fileId, { fileId: c.ref.fileId, fileName: c.ref.fileName, labels: [c.label] });
    }
    return [...byFile.values()];
  }, [citations]);

  // Which document is shown. Reset to the primary when the citations change.
  const [activeFileId, setActiveFileId] = useState(primary.ref.fileId);
  useEffect(() => setActiveFileId(primary.ref.fileId), [primary.ref.fileId]);
  const effectiveFileId = docs.some((d) => d.fileId === activeFileId) ? activeFileId : primary.ref.fileId;

  const activeCitations = useMemo(
    () => citations.filter((c) => c.ref.fileId === effectiveFileId),
    [citations, effectiveFileId],
  );

  const { data: file, isLoading, error } = useGetProceduralFile(matterId, effectiveFileId);

  const highlights = useMemo<BoundingBoxWithId[]>(
    () => activeCitations.map((c) => ({ ...c.ref.boundingBox, id: c.ref.chunkId, color: c.color })),
    [activeCitations],
  );

  const fileMetadata = useMemo(
    () => (file ? { name: file.name, extension: file.extension, pageDimensions: file.pageDimensions } : undefined),
    [file],
  );

  const multiDoc = docs.length > 1;
  const activeDoc = docs.find((d) => d.fileId === effectiveFileId);
  const activeFileName = activeDoc?.fileName ?? primary.ref.fileName;

  return (
    <>
      <div className="space-y-1 border-b px-6 py-4.5">
        <div className="flex items-center gap-3 border-gray-100">
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{activeFileName}</p>
          {multiDoc && <DocTabs docs={docs} activeFileId={effectiveFileId} onSelect={setActiveFileId} />}
          {dismissable && (
            <Button onClick={onClose} variant="ghost" size="iconSm" aria-label="Close source panel">
              <Icon className="lucide--x" />
            </Button>
          )}
        </div>

        {activeCitations.length > 1 && (
          <div className="flex flex-wrap items-center gap-4 border-gray-100 text-xs text-gray-600">
            {activeCitations.map((c) => (
              <span key={c.ref.chunkId} className="flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm opacity-50" style={{ backgroundColor: c.color }} />
                {c.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="relative flex-1 overflow-hidden">
        {error && (
          <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-500">
            Failed to load the source document.
          </div>
        )}
        {!error && (isLoading || !file) && (
          <div className="flex h-full items-center justify-center">
            <Icon className="lucide--loader-2 animate-spin text-gray-400" />
          </div>
        )}
        {file && (
          <PDFViewer
            docId={file.id}
            fileUrl={file.url}
            fileMetadata={fileMetadata}
            highlights={highlights}
            highlightedFactId={activeCitations[0]?.ref.chunkId}
            hideFeatureButtons={true}
            hideSnippingTool={true}
            hideDownload={true}
            hideFactInfo={true}
            fileName={file.name}
          />
        )}
      </div>
    </>
  );
}

/**
 * Horizontal tab strip shown when citations span more than one document, letting
 * the reviewer switch which document (and its highlights) the panel shows. Each
 * tab takes its document's primary role (own source, then original, then
 * dependent); a dependent citation only earns its own tab when it lives in a
 * document neither of the others use — otherwise its highlight just rides along.
 */
function DocTabs({
  docs,
  activeFileId,
  onSelect,
}: {
  docs: PanelDoc[];
  activeFileId: string;
  onSelect: (fileId: string) => void;
}) {
  return (
    <Tabs value={activeFileId} onValueChange={onSelect}>
      <TabsList variant="pill">
        {docs.map((d) => (
          <TabsTrigger key={d.fileId} value={d.fileId}>
            {d.labels[0]}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
